import { addDays, format, subDays } from "date-fns";
import { useMemo } from "react";
import { BidViz, Product } from "../types";

export interface ChartData {
  categories: string[];
  series: Array<{ name: string; data: number[] }>;
  dateGroups: Array<{ title: string; cols: number }>;
}

export interface BidsChartDataParams {
  bids: BidViz[];
  selectedDate: Date;
  requiredProducts: Product[];
  customFormatDate: (date: Date) => string;
}

export const useBidsChartData = ({
  bids,
  selectedDate,
  requiredProducts,
  customFormatDate,
}: BidsChartDataParams): ChartData => {
  return useMemo(() => {
    const grouped = bids.reduce(
      (
        acc: Record<string, { product_name: string; delivery_date: string; offered: number; accepted: number }>,
        bid: any,
      ) => {
        const { product_name, delivery_date, offered_quantity, accepted_quantity } = bid;
        const key = `${product_name}__${delivery_date}`;
        if (!acc[key]) {
          acc[key] = { product_name, delivery_date, offered: 0, accepted: 0 };
        }
        acc[key].offered += offered_quantity || 0;
        acc[key].accepted += accepted_quantity || 0;
        return acc;
      },
      {} as Record<string, { product_name: string; delivery_date: string; offered: number; accepted: number }>,
    );

    const uniqueDates = [
      customFormatDate(subDays(selectedDate, 1)),
      customFormatDate(selectedDate),
      customFormatDate(addDays(selectedDate, 1)),
    ];

    const fullEntries: Array<{ product_name: string; delivery_date: string; offered: number; accepted: number }> = [];
    uniqueDates.forEach((date) => {
      requiredProducts.forEach((product) => {
        const key = `${product}__${date}`;
        if (grouped[key]) {
          fullEntries.push(grouped[key]);
        } else {
          fullEntries.push({ product_name: product, delivery_date: date, offered: 0, accepted: 0 });
        }
      });
    });

    const { categories, offeredData, acceptedData } = fullEntries.reduce(
      (acc, g) => {
        acc.categories.push(`${g.product_name} – ${g.delivery_date}`);
        acc.offeredData.push(g.offered);
        acc.acceptedData.push(g.accepted);
        return acc;
      },
      { categories: [] as string[], offeredData: [] as number[], acceptedData: [] as number[] },
    );

    const series = [
      { name: "Offered Quantity", data: offeredData },
      { name: "Accepted Quantity", data: acceptedData },
    ];

    const dateGroups = uniqueDates.map((date: string, idx: number) => {
      let title;
      switch (idx) {
        case 0:
          title = format(subDays(selectedDate, 1), "dd/MM/yyyy");
          break;
        case 1:
          title = format(selectedDate, "dd/MM/yyyy");
          break;
        case 2:
          title = format(addDays(selectedDate, 1), "dd/MM/yyyy");
          break;
        default:
          title = date;
      }
      return {
        title,
        cols: requiredProducts.length,
      };
    });

    return { categories, series, dateGroups };
  }, [bids, selectedDate, requiredProducts, customFormatDate]);
};
